/**
 * Reihen fortsetzen und `...` ausfüllen – für Tabellen (keys.ts) und Listen
 * in Text/Formeln (mathAssist.ts).
 *
 * Ein Glied ist beliebiger Text mit Zahlen darin: `7`, `0,5`, `x_3`,
 * `$\frac{2}{36}$`. Glieder gehören zusammen, wenn der Text drumherum gleich
 * ist; jede Zahlenstelle wird für sich fortgesetzt (Zähler und Nenner eines
 * Bruchs also getrennt). Erkannt werden arithmetische und geometrische
 * Folgen, Polynomfolgen über das Differenzenschema (Quadratzahlen …) und
 * Fibonacci-artige (jedes Glied die Summe der beiden davor).
 *
 * Mit Endglied (`1 | ... | 6`) wird bis genau dorthin gefüllt – bei nur einem
 * Glied davor in Einerschritten, auch abwärts. Ohne Endglied entscheidet die
 * gewünschte Anzahl (in Tabellen: die Spaltenzahl der Kopfzeile).
 *
 * Bewusst ohne Importe: `node src/features/editor/series.check.ts` prüft die
 * Datei ohne Bundler.
 */

const NUM = /-?\d+(?:(?:\{,\}|[.,])\d+)?/g;
const ELLIPSIS = /^\s*(?:\.\.\.|…|\\[lc]?dots|\\ldots)\s*$/;
const EPS = 1e-9;
const MAX_TERMS = 500;

interface Term {
  /** Text zwischen den Zahlen, immer eine Stelle mehr als `nums`. */
  parts: string[];
  nums: number[];
  /** Dezimaltrenner je Zahl, wie getippt (für die Ausgabe). */
  seps: string[];
  decimals: number[];
}

function parseTerm(text: string): Term {
  const parts: string[] = [];
  const nums: number[] = [];
  const seps: string[] = [];
  const decimals: number[] = [];
  let last = 0;
  for (const m of text.matchAll(NUM)) {
    parts.push(text.slice(last, m.index));
    const sep = /\{,\}/.test(m[0]) ? "{,}" : m[0].includes(",") ? "," : ".";
    const [, frac = ""] = m[0].split(/\{,\}|[.,]/);
    nums.push(Number(m[0].replace(/\{,\}|,/, ".")));
    seps.push(sep);
    decimals.push(frac.length);
    last = m.index! + m[0].length;
  }
  parts.push(text.slice(last));
  return { parts, nums, seps, decimals };
}

function sameShape(a: Term, b: Term): boolean {
  return a.parts.length === b.parts.length && a.parts.every((p, i) => p === b.parts[i]);
}

export function isEllipsis(text: string): boolean {
  return ELLIPSIS.test(text);
}

/**
 * Bildungsregel aus mindestens zwei Gliedern; liefert das Glied an Stelle `i`
 * (0 = erstes bekanntes). `confirmed`: ein Endglied prüft die Regel ohnehin –
 * dann darf das Polynom auch aus gerade genug Gliedern geschlossen werden
 * (1, 4, 9, …, 49), sonst wäre das geraten.
 */
function ruleOf(values: number[], confirmed = false): ((i: number) => number) | null {
  const k = values.length;
  if (k < 2) return null;
  const close = (a: number, b: number) => Math.abs(a - b) < EPS * Math.max(1, Math.abs(a), Math.abs(b));

  // Arithmetisch (auch konstant).
  const d = values[1] - values[0];
  if (values.every((v, i) => i === 0 || close(v - values[i - 1], d))) return (i) => values[0] + i * d;

  // Geometrisch – erst ab drei Gliedern, sonst wäre 1, 2 nicht von 1, 2, 3 zu unterscheiden.
  if (k >= 3 && values.every((v) => v !== 0)) {
    const r = values[1] / values[0];
    if (values.every((v, i) => i === 0 || close(v / values[i - 1], r))) return (i) => values[0] * r ** i;
  }

  // Fibonacci-artig: jedes Glied die Summe der beiden davor.
  if (k >= 4 && values.every((v, i) => i < 2 || close(v, values[i - 1] + values[i - 2]))) {
    return (i) => {
      const seq = [...values];
      while (seq.length <= i) seq.push(seq[seq.length - 1] + seq[seq.length - 2]);
      return seq[i];
    };
  }

  // Polynom: Differenzenschema, bis eine Zeile konstant ist – und davon
  // muss es mindestens zwei Einträge geben, sonst ist es geraten.
  const rows: number[][] = [values];
  while (rows[rows.length - 1].length > (confirmed ? 1 : 2)) {
    const prev = rows[rows.length - 1];
    const next = prev.slice(1).map((v, i) => v - prev[i]);
    rows.push(next);
    if ((next.length > 1 || confirmed) && next.every((v) => close(v, next[0]))) {
      // Newton: f(i) = Σ C(i, j) · Δʲ f(0)
      const heads = rows.map((r) => r[0]);
      return (i) => {
        let sum = 0;
        let binom = 1;
        for (let j = 0; j < heads.length; j++) {
          sum += binom * heads[j];
          binom = (binom * (i - j)) / (j + 1);
        }
        return sum;
      };
    }
  }
  return null;
}

function format(x: number, sep: string, decimals: number): string {
  const rounded = Number(x.toFixed(Math.max(decimals, 10)));
  const text = decimals > 0 ? rounded.toFixed(decimals) : String(rounded);
  return text.replace(".", sep === "." ? "." : sep);
}

function render(shape: Term, nums: number[]): string {
  let out = shape.parts[0];
  nums.forEach((n, i) => {
    out += format(n, shape.seps[i], shape.decimals[i]) + shape.parts[i + 1];
  });
  return out;
}

/**
 * Glieder zwischen `known` (die getippten davor) und `end` (Endglied) bzw.
 * `count` neue Glieder ohne Endglied. `null`, wenn sich keine Regel findet
 * oder das Endglied nicht genau getroffen wird.
 */
function fill(known: string[], end: string | null, count: number | null): string[] | null {
  const terms = known.map(parseTerm);
  const shape = terms[terms.length - 1];
  if (!shape.nums.length || !terms.every((t) => sameShape(t, shape))) return null;
  const last = end != null ? parseTerm(end) : null;
  if (last && !sameShape(last, shape)) return null;

  // Je Zahlenstelle eine Regel. Ein einzelnes Glied mit Endglied: Einerschritte.
  const rules = shape.nums.map((_, pos) => {
    const values = terms.map((t) => t.nums[pos]);
    if (values.length > 1) return ruleOf(values, !!last);
    if (!last) return null;
    const step = Math.sign(last.nums[pos] - values[0]);
    return (i: number) => values[0] + i * step;
  });
  if (rules.some((r) => !r)) return null;

  const k = terms.length;
  const out: string[] = [];
  for (let i = k; i < k + MAX_TERMS; i++) {
    const nums = rules.map((r) => r!(i));
    if (last) {
      // Fertig, sobald alle Stellen das Endglied erreicht haben; schießt eine darüber, passt die Regel nicht.
      const reached = nums.map((n, pos) => Math.abs(n - last.nums[pos]) < EPS);
      if (reached.every(Boolean)) return out;
      const passed = nums.some((n, pos) => {
        const start = terms[0].nums[pos];
        const target = last.nums[pos];
        return (target - start) * (n - target) > EPS;
      });
      if (passed) return null;
    } else if (out.length >= (count ?? 0)) {
      return out;
    }
    out.push(render(shape, nums));
  }
  return null;
}

/**
 * Ersetzt jedes `...`/`…` in `cells` durch die passenden Glieder. Die
 * Glieder davor zählen nur, soweit sie getippt sind (bis zur Beschriftung
 * oder zum vorigen `...`) – so wird `1 | ... | 6 | ... | 1` zu 1 bis 6 und
 * wieder zurück. Ein `...` ganz am Ende füllt bis `targetLen` Zellen auf.
 */
export function expandEllipses(cells: string[], targetLen?: number): string[] | null {
  if (!cells.some(isEllipsis)) return null;
  const out: string[] = [];
  for (let i = 0; i < cells.length; i++) {
    if (!isEllipsis(cells[i])) {
      out.push(cells[i]);
      continue;
    }
    const known: string[] = [];
    for (let j = i - 1; j >= 0 && !isEllipsis(cells[j]); j--) {
      if (known.length && !sameShape(parseTerm(cells[j]), parseTerm(known[0]))) break;
      if (!parseTerm(cells[j]).nums.length) break;
      known.unshift(cells[j]);
    }
    if (!known.length) return null;
    const end = i + 1 < cells.length && !isEllipsis(cells[i + 1]) ? cells[i + 1] : null;
    const missing = end == null && targetLen ? targetLen - (cells.length - 1) : null;
    if (end == null && !missing) return null;
    const filled = fill(known, end, missing);
    if (!filled) return null;
    out.push(...filled);
  }
  return out;
}

/**
 * Wie `nextTerms`, aber schon ab zwei Gliedern – für ausdrückliches Ausfüllen
 * (Ziehen an der Markierung im Tabellen-Editor), wo niemand nur rät.
 */
export function extendTerms(terms: string[], n: number): string[] | null {
  if (terms.length < 2) return null;
  return fill(terms, null, n);
}

/** Die nächsten `n` Glieder einer getippten Reihe (mindestens drei Glieder, damit nichts geraten wird). */
export function nextTerms(terms: string[], n: number): string[] | null {
  if (terms.length < 3) return null;
  return fill(terms, null, n);
}
